import { Trans } from '@lingui/react'
import classNames from 'classnames'
import { bool, oneOf, shape, string } from 'prop-types'

import { PRICE_TYPES } from 'shared/consts/productTemplates'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import Price from 'views/components/atoms/Price'
import PriceFrom from 'views/components/atoms/PriceFrom'
import PriceOld from 'views/components/atoms/PriceOld'
import { COMPONENT_SIZE } from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  isListPriceGreaterThanOrEqualToRetailPrice,
  isPricePresent,
} from 'views/utils/productUtils'
import { PriceListType_v3 as PriceListType } from 'views/utils/propTypes'

import PriceInfo from './PriceInfo'

import './ProductPrice.scss'

const SIZE_TO_MARGINS = {
  'x-small': 'u-margin-x-small--right',
  small: 'u-margin-large--right',
  default: 'u-margin--right',
  large: 'u-margin-small--right',
}

const renderPriceInfo = ({ info, size, stacked }) => {
  const className = classNames(
    'o-ProductPrice__info',
    'u-vertical-align--super@medium',
    'u-display--block@small-tiny',
    {
      'o-ProductPrice__info--stacked': stacked,
      [SIZE_TO_MARGINS[size]]: !stacked,
    }
  )

  return (
    <div className={className}>
      <PriceInfo info={info} />
    </div>
  )
}

const renderPrice = ({
  price,
  size,
  displayInfo,
  infoStacked,
  priceComponentProps,
  PriceComponent = Price,
  isRebrandingEnabled,
}) => (
  <>
    {displayInfo &&
      price.info &&
      !isRebrandingEnabled &&
      renderPriceInfo({ info: price.info, size, stacked: infoStacked })}
    <PriceComponent
      amount={price.amount}
      currency={price.currency}
      {...priceComponentProps}
    />
  </>
)

renderPrice.propTypes = {
  price: shape({ info: string }).isRequired,
  isRebrandingEnabled: bool,
}

const ProductPrice = props => {
  const {
    prices,
    infoStacked,
    stacked,
    size,
    displayInfo,
    className,
    stackedDesktop,
    prescriptionMedicine,
    multipleOffer,
    showSuperScript,
    unitPricing,
    centered,
    publicRuntimeConfig: {
      publicConfig: { superScripts },
      pageProperties: { experiments },
    },
    shouldDisplayAbPrice,
  } = props

  const isRebrandingEnabled = isRebrandingActive(experiments)

  const listPrice = getPriceForType(prices, PRICE_TYPES.LIST)
  const retailPrice = getPriceForType(prices, PRICE_TYPES.RETAIL)

  const classes = classNames(
    {
      [`${className}`]: className,
      'u-text-align--center': centered,
      'u-text-align--right': !centered,
    },
    'o-ProductPrice',
    {
      'a-h4': size === 'x-small',
      'a-h3': size === 'small',
      'a-h2': !size || size === 'default',
      'a-h1': size === 'large',
    },
    'u-no-margin',
    'u-text-align--left@small-tiny',
    'u-font-weight--normal'
  )

  const priceClassName = 'u-display--inline-block'
  const listClassName = classNames(
    priceClassName,
    'o-ProductPrice__list-price',
    {
      [SIZE_TO_MARGINS[size]]: !stacked,
      'u-no-margin--right@medium': stackedDesktop,
    }
  )
  const retailClassName = classNames(
    priceClassName,
    'o-ProductPrice__entry-price',
    {
      'u-font-weight--bold': size === 'x-small',
    }
  )

  const isListPricePresent = isPricePresent(listPrice)
  const isRetailPricePresent = isPricePresent(retailPrice)

  let ListPriceComponent
  const listPriceComponentProps = {}

  if (isListPricePresent && isRetailPricePresent) {
    ListPriceComponent = PriceOld

    if (listPrice.amount === retailPrice.amount) {
      listPriceComponentProps.noLineThrough = true
    }

    if (showSuperScript && superScripts && superScripts.avp) {
      listPriceComponentProps.superScript = superScripts.avp
    }
  }

  let RetailPriceComponent
  if (isRetailPricePresent && retailPrice.amount > 0 && multipleOffer) {
    RetailPriceComponent = PriceFrom
  }

  return (
    <div className="u-display--inline-block o-ProductPrice__wrapper">
      <div className={classes}>
        {!prescriptionMedicine &&
          isListPriceGreaterThanOrEqualToRetailPrice({
            listPrice,
            retailPrice,
          }) && (
            <>
              <div className={listClassName}>
                {renderPrice({
                  price: listPrice,
                  size,
                  displayInfo,
                  infoStacked,
                  PriceComponent: ListPriceComponent,
                  priceComponentProps: listPriceComponentProps,
                  isRebrandingEnabled,
                })}
              </div>
              {stacked && <br />}
              {stackedDesktop && (
                <br className="u-hidden@small-tiny o-ProductPrice__divider" />
              )}
            </>
          )}

        {isRetailPricePresent && retailPrice.amount > 0 && (
          <div className={retailClassName} data-qa-id="entry-price">
            {shouldDisplayAbPrice && (
              <>
                <Trans id="general.label.price.from" />{' '}
              </>
            )}

            {renderPrice({
              price: retailPrice,
              size,
              displayInfo,
              infoStacked,
              PriceComponent: RetailPriceComponent,
            })}
          </div>
        )}

        {Boolean(unitPricing) && (
          <div className="a-p8 u-no-margin u-no-wrap u-color--grey u-text-align--center o-ProductPrice__unit-price">
            {unitPricing}
          </div>
        )}
      </div>
    </div>
  )
}

ProductPrice.propTypes = {
  prices: PriceListType.isRequired,
  size: oneOf(COMPONENT_SIZE),
  stacked: bool,
  infoStacked: bool,
  displayInfo: bool,
  className: string,
  stackedDesktop: bool,
  prescriptionMedicine: bool,
  multipleOffer: bool,
  publicRuntimeConfig: shape({}),
  showSuperScript: bool,
  centered: bool,
  unitPricing: string,
  shouldDisplayAbPrice: bool,
}

ProductPrice.defaultProps = {
  size: 'default',
  stacked: false,
  infoStacked: false,
  displayInfo: true,
  className: null,
  stackedDesktop: false,
  prescriptionMedicine: false,
  multipleOffer: false,
  publicRuntimeConfig: {},
  showSuperScript: false,
  centered: false,
  unitPricing: null,
  shouldDisplayAbPrice: false,
}

export default withShopAndRuntimeConfig(ProductPrice)
